/**
 * fleet-agreement-acceptance (path 1): mandatory (checkbox) documents a
 * customer must explicitly accept before a soft hold can be created for a
 * vehicle. Kept as a pure function so the "can the customer continue?" gate
 * is testable without mounting `CheckoutContainer` and its Stripe/react-query
 * dependency tree.
 */
export interface MandatoryDocumentLike {
  document_version_id: string;
}

export function allDocumentsAccepted(
  documents: readonly MandatoryDocumentLike[],
  acceptedVersionIds: ReadonlySet<string>,
): boolean {
  return documents.every((document) =>
    acceptedVersionIds.has(document.document_version_id),
  );
}

/**
 * Whether checkout must stay blocked on mandatory documents.
 *
 * `useAcceptanceDocuments` defaults `data` to `[]` while its query is still
 * in flight, and `allDocumentsAccepted([], ...)` is vacuously true — so
 * gating on that alone would open the submit button for the brief window
 * before the vehicle's actual documents have loaded, letting a fast click
 * skip them entirely. Block unconditionally while loading; once loaded,
 * fall back to `allDocumentsAccepted` (including the documents-endpoint's
 * own failure case, which intentionally fails open — see
 * `AcceptanceDocumentsSection`'s doc comment).
 */
export function hasUnacceptedMandatoryDocuments(
  isLoading: boolean,
  documents: readonly MandatoryDocumentLike[],
  acceptedVersionIds: ReadonlySet<string>,
): boolean {
  if (isLoading) return true;
  return !allDocumentsAccepted(documents, acceptedVersionIds);
}
